<template>
  <div>
    <div class="d-flex justify-content-between align-items-center">
      <div>
        <h1 class="text-uppercase">
          <span>{{ isEditPage ? $t('edit') : $t('create') }} {{ $t('native_open_campaign') }}</span>
        </h1>
      </div>
      <div class="d-flex flex-row justify-content-center align-items-center gap-3 me-2">
        <div class="d-flex flex-row justify-content-center align-items-center gap-2" v-if="isSavingDraft">
          <Loader
            :size="'small'"
            :align="'start'"
          />
          {{ $t('saving_draft') }}
        </div>
        <Button
          class="btn btn-color10"
          v-b-modal.cancel-campaign-upsert-modal
        >
          {{ $t('cancel') }}
        </Button>
        <CancelCampaignUpsertModal
          :campaign="campaign"
          step="campaign"
        />
      </div>
    </div>
    <!-- CAMPAIGN FORM -->
    <div class="mt-3" v-if="!isEditPage || (isEditPage && campaign)">
      <div class="campaign-wrapper position-relative">
        <!-- INIT STEP -->
        <template v-if="step === 'init'">
          <div class="col-lg-8 mx-auto">
            <div class="card border-dashed border-color10">
              <div class="card-body">
                <p>
                  {{ $t('easy_campaign_pixel_roads') }}
                </p>
                <p>
                  {{ $t('important_thoughts_for_campaign') }}
                </p>
                <ul>
                  <li>{{ $t('available_budget') }}*<br/>
                    <span class="font-size-xSmall">* {{ $t('daily_budget_calculation') }}</span>
                  </li>
                  <li>{{ $t('creatives_in_jpeg_or_png_format_1200x627_627x627') }}</li>
                  <li>{{ $t('more_about_formats.0') }} <a href="https://pixelroads.com.br/blog/transforme-suas-campanhas-dicas-para-criar-banners-que-convertem/" target="_blank" class="link-color3">{{ $t('more_about_formats.1') }}</a>;</li>
                  <li>{{ $t('suggest_pixel_pre_installed') }}</li>
                </ul>
                <div class="mt-4 text-center">
                  <Button
                    class="btn btn-color10"
                    @click="changeStep('campaign')"
                  >
                    {{ $t('start2') }}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </template> <!-- END INIT STEP -->
        <!-- HEADING -->
        <section class="my-4" v-if="step !== 'init'">
          <div class="text-center">
            <p>
              {{ stepDescription }}
              <span
                v-b-tooltip.hover
                :title="tooltips.pixels"
                v-if="step === 'tracking'"
              >
                <i class="fa fa-info-circle font-size-xRegular"></i>
              </span>
            </p>
            <div class="mb-3 text-start">
              <a href="#" class="link-color2" @click.prevent="changeStep(previousStep)" v-if="previousStep"><i class="fa fa-angle-left me-2"></i>{{ $t('back') }}</a>
            </div>
          </div>
        </section> <!-- END HEADING -->
        <!-- CAMPAIGN STEP -->
        <template v-if="step === 'campaign'">
          <section class="">
            <div class="accordion accordion-flush" id="accordionFlushExample">
              <!-- FORM CAMPAIGN ACCORDION ITEM -->
              <div class="accordion-item border-bottom border-dashed">
                <h2 class="accordion-header">
                  <button class="accordion-button text-uppercase font-size-xMedium" block v-b-toggle.accordion-1 variant="info">
                    {{ $t('campaign') }}
                  </button>
                </h2>
                <b-collapse id="accordion-1" visible accordion="my-accordion" role="tabpanel">
                  <div class="accordion-body card">
                    <form @submit.prevent="" class="default-form">
                      <div class="d-flex flex-column flex-md-row">
                        <div class="col-12 col-md-6 pe-md-10">
                          <!-- GENERAL DATA HEADER -->
                          <div class="mb-2">
                            <div class="d-flex align-items-center justify-content-between">
                              <h3 class="text-uppercase font-size-regular fw-500">
                                <span>{{ $t('general_campaign_data') }}</span>
                              </h3>
                            </div>
                          </div> <!-- END GENERAL DATA HEADER -->
                          <!-- ADVERTISER -->
                          <div class="form-group mb-3">
                            <label class="form-label fw-bold">
                              {{ $t('advertiser') }}*
                            </label>
                            <div v-if="advertisersLoader">
                              <Loader
                                :size="'small'"
                                :align="'start'"
                              />
                            </div>
                            <template v-else>
                              <div class="custom-select">
                                <select
                                  class="form-control"
                                  v-model.trim="form.advertiser_id"
                                  :disabled="campaign !== null && campaign.status !== 'DRAFT'"
                                >
                                  <option disabled value="">{{ $t('select') }}</option>
                                  <template v-if="advertisers.length > 0">
                                    <option v-for="(lAdvertiser, index) in advertisers" :key="index" :value="lAdvertiser.id">{{ lAdvertiser.name }}</option>
                                  </template>
                                </select>
                              </div>
                              <p
                                class="font-size-xSmall fw-bold mt-2 error-message-card"
                                v-if="selectedAdvertiser && selectedAdvertiser.status === 'ERROR'"
                              >
                                {{ $t('advertiser_creation_issue_access_page_try_again', {name:selectedAdvertiser.name}) }}
                              </p>
                            </template>
                          </div> <!-- END ADVERTISER -->
                          <!-- NAME -->
                          <div class="form-group mb-3">
                            <label class="form-label fw-bold">{{ $t('campaign_name') }}*</label>
                            <input type="text" class="form-control" :placeholder="$t('campaign_name')" v-model.trim="form.name" />
                          </div> <!-- END NAME -->
                          <!-- DATES -->
                          <div class="form-group mb-7">
                            <label class="form-label fw-bold">{{ $t('duration') }}*</label>
                            <div class="d-flex">
                              <!-- START DATE -->
                              <div class="col-lg-4">
                                <DateTimeInput
                                  v-model.trim="form.start_date"
                                  format="dd/MM/yyyy"
                                  type="date"
                                  theme="basic"
                                  class="mx-2"
                                  :placeholder="$t('start')"
                                  :readonly="campaign && campaign.has_started"
                                />
                              </div> <!-- END START DATE -->
                              <!-- END DATE -->
                              <div class="col-lg-4">
                                <DateTimeInput
                                  v-model.trim="form.end_date"
                                  format="dd/MM/yyyy"
                                  type="date"
                                  theme="basic"
                                  class="mx-2"
                                  :placeholder="$t('end')"
                                />
                              </div> <!-- END END DATE -->
                            </div>
                            <p
                              class="font-size-xSmall fw-bold mt-2 error-message-card"
                              v-if="form.end_date && !isCampaignEndDateValid"
                            >
                              {{ $t('end_date_validation') }}
                            </p>
                            <p
                              class="font-size-xSmall fw-bold mt-2 error-message-card"
                              v-if="form.start_date && form.end_date && isCampaignEndDateBeforeStartDate"
                            >
                              {{ $t('end_date_not_before_start_date') }}
                            </p>
                            <p
                              class="font-size-xSmall fw-bold mt-2 alert-message-card"
                              v-if="form.start_date && form.end_date && isCampaignPeriodBelowRecommended"
                            >
                              {{ $t('campaign_minimum_duration_10_days') }}
                            </p>
                          </div> <!-- END DATES -->
                        </div>

                        <div class="col-12 col-md-6 ps-md-5">
                          <!-- GENERAL DATA HEADER -->
                          <div class="mb-2">
                            <div class="d-flex align-items-center justify-content-between">
                              <h3 class="text-uppercase font-size-regular fw-500">
                                <span>{{ $t('budget') }}</span>
                              </h3>
                            </div>
                          </div> <!-- END GENERAL DATA HEADER -->
                          <!-- AMOUNT -->
                          <div class="form-group mb-3">
                            <label class="form-label fw-bold">{{ $t('campaign_budget') }}*</label>
                            <Money
                              class="form-control mb-3"
                              v-model.trim="form.amount"
                              v-bind="money"
                            />
                            <template v-if="selectedAdvertiser">
                              <div class="d-flex flex-column flex-wrap">
                                <div class="">
                                  <p
                                    class="font-size-xSmall fw-bold mt-2 error-message-card"
                                    v-if="hasCampaignExceededAvailableAdvertiserAmount"
                                  >
                                    {{ $t('campaign_budget_cannot_exceed') }}
                                  </p>
                                  <p class="font-size-xSmall fw-bold">
                                    {{ $t('available_balance') }}: {{ selectedAdvertiser.available_amount / 100 | formatMoney(selectedAdvertiser.currency) }}
                                  </p>
                                </div>
                                <div class="text-lg-end d-flex align-items-center justify-content-center justify-content-lg-end">
                                  <Button
                                    class="btn btn-color10 btn-xs me-2"
                                    @click="addAllAvailableAmountToCampaign"
                                  >
                                    {{ $t('use_all_balance') }}
                                  </Button>
                                </div>
                              </div>
                            </template>
                            <p
                              class="font-size-xSmall fw-bold mt-2 error-message-card"
                              v-if="!checkIfDailyBudgetIsBiggerThanMinimumDailyBudget"
                            >
                              {{ $t('recommended_minimum_daily_budget', {value:minimumDailyBudget, date:this.$options.filters.formatDate(suggestedEndDate)}) }}
                            </p>
                          </div><!-- END AMOUNT -->
                          <!-- CPC TARGET AMOUNT -->
                          <div class="form-group mb-3">
                            <label class="form-label fw-bold">{{ $t('cpc_goal') }}* <span v-b-tooltip.hover.html :title="tooltips.cpc_goal"><i class="fa fa-info-circle font-size-xRegular"></i></span></label>
                            <Money
                              class="form-control mb-3"
                              v-model.trim="form.cpc_target_amount"
                              v-bind="money"
                            />
                            <p class="font-size-xSmall fw-bold error-message-card" v-if="form.cpc_target_amount !== '' && form.cpc_target_amount < 3">
                              {{ $t('native_minimum_cpc_goal_value') }} {{ 3 | formatMoney(selectedAdvertiser ? selectedAdvertiser.currency : null) }}
                            </p>
                          </div><!-- END CPC TARGET AMOUNT -->
                        </div>
                      </div>
                      <div class="separator separator-dashed border-color10 my-2"></div>
                      <div class="d-flex flex-column flex-md-row">
                        <div class="col-12 col-md-6 pe-md-10">
                          <!-- GENERAL DATA HEADER -->
                          <div class="mb-2 mt-4">
                            <div class="d-flex align-items-center justify-content-between">
                              <h3 class="text-uppercase font-size-regular fw-500">
                                <span>{{ $t('segmentation') }}</span>
                              </h3>
                            </div>
                          </div> <!-- END GENERAL DATA HEADER -->
                          <!-- BROWSER -->
                          <div class="form-group mb-3 me-2">
                            <label class="form-label fw-bold w-100 d-block">{{ $t('devices') }}*</label>
                            <div class="form-check form-check-inline">
                              <input
                                class="form-check-input"
                                type="checkbox"
                                :id="`browser-mobile`"
                                value="MOBILE"
                                v-model="form.browser"
                              >
                              <label class="form-check-label" :for="`browser-mobile`">{{ $t('mobile') }}</label>
                            </div>
                            <div class="form-check form-check-inline">
                              <input
                                class="form-check-input"
                                type="checkbox"
                                :id="`browser-desktop`"
                                value="DESKTOP"
                                v-model="form.browser"
                              >
                              <label class="form-check-label" :for="`browser-desktop`">{{ $t('desktop') }}</label>
                            </div>
                          </div> <!-- END BROWSER -->
                          <div class="">
                            <!-- COUNTRIES -->
                            <div class="form-group mb-3 me-2">
                              <div class="d-flex align-items-center mb-2">
                                <label class="form-label fw-bold mb-0">
                                  {{ $t('countries') }}
                                  <span v-b-tooltip.hover :title="tooltips.locations"><i class="fa fa-info-circle font-size-xRegular"></i></span>
                                </label>
                              </div>
                              <template v-if="countriesLoader">
                                <Loader
                                  :size="'small'"
                                  :align="'start'"
                                />
                              </template>
                              <template v-else>
                                <Multiselect
                                  v-model="selectedCountries"
                                  :placeholder="$t('search2')"
                                  label="label"
                                  track-by="code"
                                  :options="countryOptions"
                                  :multiple="true"
                                />
                              </template>
                            </div> <!-- END COUNTRIES -->
                            <!-- STATES -->
                            <div class="form-group mb-3 me-2" v-if="selectedCountries.length > 0">
                              <div class="d-flex align-items-center mb-2">
                                <label class="form-label fw-bold mb-0">
                                  {{ $t('states') }}
                                  <span v-b-tooltip.hover :title="tooltips.locations"><i class="fa fa-info-circle font-size-xRegular"></i></span>
                                </label>
                                <Button
                                  class="btn-xs btn-color10 py-1 ms-2"
                                  @click="selectAllStates"
                                  v-if="stateOptions.length > 0"
                                >
                                  {{ $t('select_all') }}
                                </Button>
                                <Button
                                  class="btn-xs btn-color10 ms-2 py-1"
                                  @click="clearSelection('states')"
                                  v-if="form.states.length > 0"
                                >
                                  {{ $t('clear') }}
                                </Button>
                              </div>
                              <template v-if="statesLoader">
                                <Loader
                                  :size="'small'"
                                  :align="'start'"
                                />
                              </template>
                              <template v-else>
                                <Multiselect
                                  v-model="form.states"
                                  :placeholder="$t('search2')"
                                  label="label"
                                  track-by="code"
                                  :options="stateOptions"
                                  :multiple="true"
                                  :disabled="selectedCountries.length == 0"
                                />
                                <div
                                  class="mt-2 alert-message-card text-color2 font-size-small px-3 py-2"
                                  v-if="selectedCountries.length == 0"
                                >
                                  <p
                                    class="mb-0"
                                  >
                                    {{ $t('select_at_least_one_country_for_state_selection') }}
                                  </p>
                                </div>
                              </template>
                            </div> <!-- END STATES -->
                            <!-- CITIES -->
                            <div class="form-group mb-3" v-if="selectedCountries.length > 0">
                              <label class="form-label fw-bold">
                                {{ $t('cities') }}
                                <span v-b-tooltip.hover :title="tooltips.locations"><i class="fa fa-info-circle font-size-xRegular"></i></span>
                              </label>
                              <template v-if="citiesLoader">
                                <Loader
                                  :size="'small'"
                                  :align="'start'"
                                />
                              </template>
                              <template v-else>
                                <Multiselect
                                  v-model="form.cities"
                                  :placeholder="$t('search2')"
                                  label="label"
                                  track-by="code"
                                  :options="citiesOptions"
                                  :multiple="true"
                                  :disabled="selectedCountries.length == 0"
                                />
                                <div
                                  class="mt-2 alert-message-card text-color2 font-size-small px-3 py-2"
                                  v-if="selectedCountries.length == 0"
                                >
                                  <p
                                    class="mb-0"
                                  >
                                    {{ $t('select_at_least_one_country_for_city_selection') }}
                                  </p>
                                </div>
                              </template>
                            </div> <!-- END CITIES -->
                            <div v-if="showAlertOnCitiesAndStatesSelection && (!countriesLoader && !citiesLoader && !statesLoader)">
                              <p class="fw-bold alert-message-card text-color2 font-size-xRegular mt-2" v-html="showAlertOnCitiesAndStatesSelection">
                              </p>
                            </div>
                          </div>
                        </div>
                        <div class="col-12 col-md-6 ps-md-5">
                          <!-- CATEGORIES -->
                          <div class="form-group mb-3 mt-5">
                            <label class="form-label fw-bold">{{ $t('categories2') }} <span v-b-tooltip.hover.html :title="$t('select_broad_categories')"><i class="fa fa-info-circle font-size-xRegular"></i></span></label>
                            <div v-if="categoriesLoader">
                              <Loader
                                :size="'small'"
                                :align="'start'"
                              />
                            </div>
                            <div class="custom-select" v-else>
                              <Multiselect
                                v-model="form.categories"
                                :placeholder="$t('search2')"
                                label="label"
                                track-by="code"
                                :options="categoriesOptions"
                                :multiple="true"
                              />
                            </div>
                          </div><!-- CATEGORIES -->
                          <!-- CUSTOM AUDIENCES LIST -->
                          <div class="form-group mb-3 mt-10" v-if="selectedAdvertiser">
                            <p>{{ $t('check_if_you_have_remarketing_audiences_available') }}</p>
                            <label class="form-label fw-bold w-100 d-block">
                              {{ $t('own_audience_list') }} <span v-b-tooltip.hover.html :title="tooltips.own_audiences"><i class="fa fa-info-circle font-size-xRegular"></i></span> <a href="#" @click.prevent="" class="fw-normal ms-2" v-b-modal.select-custom-targeting-emails-modal v-if="selectedAdvertiser.is_custom_audiences_enabled">{{ $t('edit') }}</a>
                            </label>
                            <template v-if="!selectedAdvertiser.is_custom_audiences_enabled">
                              <p>{{ $t('own_audiences_only_available_for_advertisers_over_3_months') }}</p>
                            </template>
                            <template v-else-if="form.custom_audiences.length === 0">
                              <p>{{ $t('zero_own_audiences_selected') }}</p>
                            </template>
                            <template v-else>
                              <ul>
                                <li
                                  class="d-flex justify-content-between align-items-center mb-3 col-lg-7"
                                  v-for="(audience, index) in form.custom_audiences"
                                  :key="index"
                                >
                                  <span>
                                    {{ audience.name}}
                                  </span>
                                  <div>
                                    <Button
                                      class="btn-xs btn-circle btn-danger font-size-small p-0"
                                      @click="destroyCustomTargetings(index, 'custom_audiences', audience)"
                                      v-b-tooltip.hover :title="$t('remove')"
                                    >
                                      <i class="fa fa-trash px-3"></i>
                                    </Button>
                                  </div>
                                </li>
                              </ul>
                            </template>
                            <SelectCustomTargetingModal
                              :advertiser="selectedAdvertiser"
                              modalId="select-custom-targeting-emails-modal"
                              type="EMAIL_AUDIENCE"
                              @custom-targeting-selected="handleCustomAudienceSelected"
                            />
                          </div> <!-- END CUSTOM AUDIENCES LIST -->
                          <div v-if="selectedAdvertiser && form.amount && form.custom_audiences.length > 0">
                            <label for="budget-allocation" class="form-label fw-bold">{{ $t('budget_allocation') }}</label>
                            <input type="range" class="form-range" min="0" max="100" step="5" id="budget-allocation" v-model="form.allocated">
                            <div class="d-flex justify-content-between px-5">
                              <div>
                                <strong>Contextual</strong>
                                <p>R$ {{ (form.amount * (form.allocated / 100)).toFixed(2) }}</p>
                              </div>
                              <p>{{ form.allocated }}%</p>
                              <div>
                                <strong>Remarketing</strong>
                                <p class="text-end">R$ {{ (form.amount * ((100 - form.allocated) / 100)).toFixed(2) }}</p>
                              </div>
                            </div>
                          </div>
                          <!-- BLACKLIST -->
                          <div class="form-group mb-3" v-if="selectedAdvertiser">
                            <label class="form-label fw-bold w-100 d-block">
                              {{ $t('custom_targeting_type.blacklist') }}
                              <span v-b-tooltip.hover :title="tooltips.site_lists"><i class="fa fa-info-circle font-size-xRegular"></i></span>
                              <a href="#" @click.prevent="" class="fw-normal ms-2" v-b-modal.select-custom-targeting-blacklist-modal>{{ $t('edit') }}</a>
                            </label>
                            <template v-if="form.blacklist.length === 0">
                              <p>{{ $t('zero_lists_selected') }}</p>
                            </template>
                            <template v-else>
                              <ul>
                                <li class="d-flex justify-content-between align-items-center mb-3 col-lg-7" v-for="(list, index) in form.blacklist" :key="index">
                                  <span>
                                    {{ list.name}}
                                  </span>
                                  <div>
                                    <Button
                                      class="btn-xs btn-circle btn-danger font-size-small p-0"
                                      @click="destroyCustomTargetings(index, 'blacklist')"
                                      v-b-tooltip.hover :title="$t('remove')"
                                    >
                                      <i class="fa fa-trash px-3"></i>
                                    </Button>
                                  </div>
                                </li>
                              </ul>
                            </template>
                            <SelectCustomTargetingModal
                              :advertiser="selectedAdvertiser"
                              modalId="select-custom-targeting-blacklist-modal"
                              :type="['SITE_LIST', 'APP_LIST']"
                              @custom-targeting-selected="handleBlacklistSelected"
                            />
                          </div> <!-- END BLACKLIST -->
                        </div>
                      </div>
                      <!-- ACTION -->
                      <div class="mt-4 text-center">
                        <Button
                          class="btn btn-color10"
                          @click="changeAccordionItem('accordion-2')"
                          :disabled="!isCampaignStepComplete || !checkIfDailyBudgetIsBiggerThanMinimumDailyBudget"
                        >
                          {{ $t('next') }}
                        </Button>
                      </div> <!-- END ACTION -->
                    </form>
                  </div>
                </b-collapse>
              </div><!-- END FORM CAMPAIGN ACCORDION ITEM -->

              <!-- CREATIVE ACCORDION ITEM -->
              <div class="accordion-item border-bottom border-dashed">
                <h2 class="accordion-header">
                  <button class="accordion-button text-uppercase font-size-xMedium" type="button" block v-b-toggle.accordion-2 variant="info" :disabled="!isCampaignStepComplete || !checkIfDailyBudgetIsBiggerThanMinimumDailyBudget">
                    {{ $t('creatives') }}
                  </button>
                </h2>
                <b-collapse id="accordion-2" accordion="my-accordion" role="tabpanel">
                  <div class="accordion-body card">
                    <div class="text-center">
                      <h2 class="highlighted-color1">
                        <span>{{ $t('select_creative') }}</span>
                      </h2>
                      <p>{{ $t('select_creative_or_create') }}</p>
                    </div>

                    <section class="">
                      <div class="d-flex flex-column flex-lg-row justify-content-center mb-5">
                        <div class="col-12 col-lg-6 pe-3">
                          <!-- RECTANGULAR HEADER -->
                          <div class="mb-2">
                            <div class="d-flex align-items-center justify-content-center">
                              <h3 class="text-uppercase font-size-xMedium">
                                <span>{{ $t('creative') }} 1200x627*</span>
                              </h3>
                            </div>
                          </div> <!-- END RECTANGULAR HEADER -->
                          <div class="mt-4">
                            <!-- CREATIVE -->
                            <div
                              class="d-flex creative-item flex-wrap align-items-center justify-content-end mb-5 border border-dashed p-5"
                              v-for="(creative, index) in form.creatives" :key="index"
                            >
                              <div class="col-lg-1">
                                <Button
                                  class="btn-xs btn-circle btn-danger font-size-small p-0"
                                  @click="openDestroyCreativeModal(creative, 'creative')"
                                  v-b-tooltip.hover :title="$t('remove_creative')"
                                >
                                  <i class="fa fa-trash px-3"></i>
                                </Button>
                              </div>
                              <CreativePreviewWrapper
                                v-if="form.creatives[index]"
                                :advertiser="selectedAdvertiser"
                                :creative="form.creatives[index]"
                                :index="index"
                                size="small"
                              />
                            </div> <!-- END CREATIVE -->
                            <SelectCreative
                              v-if="form.advertiser_id"
                              :advertiserId="form.advertiser_id"
                              format="NATIVE_DISPLAY"
                              layout="1200x627"
                              hidePreview="true"
                              @creative-selected="handleSelectedCreatives"
                            />
                          </div>
                        </div>

                        <div class="col-12 col-lg-6 ps-3">
                          <!-- RECTANGULAR HEADER -->
                          <div class="mb-2">
                            <div class="d-flex align-items-center justify-content-center">
                              <h3 class="text-uppercase font-size-xMedium">
                                <span>{{ $t('creative') }} 627x627*</span>
                              </h3>
                            </div>
                          </div> <!-- END RECTANGULAR HEADER -->
                          <div class="mt-4">
                            <!-- CREATIVE -->
                            <div
                              class="d-flex creative-item flex-wrap align-items-center justify-content-end mb-5 border border-dashed p-5"
                              v-for="(creative, index) in form.square_creatives" :key="index"
                            >
                              <div class="col-lg-1">
                                <Button
                                  class="btn-xs btn-circle btn-danger font-size-small p-0"
                                  @click="openDestroyCreativeModal(creative, 'square_creative')"
                                  v-b-tooltip.hover :title="$t('remove_creative')"
                                >
                                  <i class="fa fa-trash px-3"></i>
                                </Button>
                              </div>
                              <CreativePreviewWrapper
                                v-if="creative"
                                :advertiser="selectedAdvertiser"
                                :creative="creative"
                                :index="index"
                                size="small"
                              />
                            </div> <!-- END CREATIVE -->
                            <SelectCreative
                              v-if="form.advertiser_id"
                              :advertiserId="form.advertiser_id"
                              format="NATIVE_DISPLAY"
                              layout="627x627"
                              :hidePreview="true"
                              @creative-selected="handleSelectedCreatives"
                            />
                          </div>
                        </div>
                      </div>
                    </section>
                    <DestroyCreativeModal
                      :creative="selectedCreative"
                      @creative-destroyed="destroyCreative"
                    />

                    <!-- ACTIONS -->
                    <div class="text-center mt-10">
                      <Button
                        class="btn btn-color10"
                        @click="changeAccordionItem('accordion-3')"
                        :disabled="!isCreativeStepComplete"
                      >
                        {{ $t('next') }}
                      </Button>
                    </div> <!-- END ACTION -->
                  </div>
                </b-collapse>
              </div><!-- END CREATIVE ACCORDION ITEM -->

              <!-- PIXEL ACCORDION ITEM -->
              <div class="accordion-item border-bottom border-dashed">
                <h2 class="accordion-header">
                  <button class="accordion-button text-uppercase font-size-xMedium" block v-b-toggle.accordion-3 variant="info" :disabled="!isCampaignStepComplete">
                    {{ $t('pixels') }}
                  </button>
                </h2>
                <b-collapse id="accordion-3" accordion="my-accordion" role="tabpanel">
                  <div class="accordion-body card">
                    <div class="mb-5">
                      <p
                        class="text-center font-size-xSmall fw-bolder mt-2 alert-message-card"
                      >
                        {{ $t('select_pixel_message') }}
                      </p>
                      <p
                        class="text-center font-size-xSmall fw-bolder mt-2"
                      >
                        {{ $t('learn_more_about_pixel') }}, <a href="https://pixelroads.com.br/blog/pixel-academy/1-8-1-explicacao-de-pixels/" target="_blank">{{ $t('click_here') }}</a>
                      </p>
                      <!-- ACTION -->
                      <div class="text-center" v-if="selectedAdvertiser">
                        <router-link
                          :to="{ name: 'pixels.index' }"
                          class="btn btn-color10 mb-4 me-2 btn-sm"
                          target="_blank"
                        >
                          {{ $t('add_new_pixel') }}
                        </router-link>
                        <div class="border border-dashed border-color10 p-5">
                          <SelectPixelRule
                            :advertiserId="form.advertiser_id"
                            @pixel-rule-selected="handleSelectedPixelRule"
                          />
                        </div>
                      </div> <!-- END ACTION -->
                      <template v-if="form.pixels.length > 0">
                        <div class="mt-4 text-center border border-dashed border-color10 p-5">
                          <h2 class="text-uppercase font-size-regular fw-500">
                            <span>{{ $t('selected_pixels') }}</span>
                          </h2>
                          <table class="table table-striped align-middle table-row-dashed fs-6 gy-2 dataTable no-footer">
                            <thead>
                              <tr>
                                <th>{{ $t('pixel_name') }}</th>
                                <th>{{ $t('rule_name') }}</th>
                                <th>{{ $t('objective') }}</th>
                                <th>{{ $t('event_name') }}</th>
                                <th width="100">{{ $t('actions') }}</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr class="text-nowrap" v-for="(rule, index) in form.pixels" :key="index">
                                <td>{{ rule.pixel.name }}</td>
                                <td>{{ rule.name }}</td>
                                <td>{{ rule.objective | formatObjective }}</td>
                                <td>{{ rule.event_name }}</td>
                                <td>
                                  <Button
                                    class="btn-danger btn-xs font-size-small btn-circle p-0 mb-2"
                                    @click.prevent="openDestroyPixelRuleModal(rule)"
                                    v-b-tooltip.hover :title="$t('remove_rule')"
                                  >
                                    <i class="fa fa-trash px-3"></i>
                                  </Button>
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </template>
                      <DestroyPixelRuleModal
                        :rule="selectedPixelRule"
                        @pixel-rule-destroyed="destroyPixelRule"
                      />
                    </div>
                  </div>
                </b-collapse>
              </div><!-- END PIXEL ACCORDION ITEM -->
            </div>
            <div class="mt-5 text-center">
              <!-- MESSAGES -->
              <div
                class="mb-2 alert-message-card text-color2 font-size-small px-3 py-2"
                v-if="loader"
              >
                <p
                  class="mb-0"
                >
                  {{ $t('this_action_may_take_a_few_minutes') }}
                </p>
              </div> <!-- END MESSAGES -->
              <Button
                class="btn btn-color10"
                @click="clickSubmit"
                :disabled="!isFormComplete || isSavingDraft"
                :loading="loader"
              >
                {{ campaign ? $t('save_campaign') : $t('create_campaign') }}
              </Button>
            </div>
          </section>
        </template> <!-- END CAMPAIGN STEP -->
      </div>
    </div> <!-- END CAMPAIGN FORM -->
  </div>
</template>

<script>

import AdvertisersService from '@/modules/advertisers/services/advertisers-service'
import CampaignsService from '@/modules/campaigns/services/campaigns-service'
import CategoriesService from '@/modules/categories/services/categories-service'
import { getMoneyFormat } from '@/utils'
import HelpersService from '@/v1/modules/helpers/services/helpers-service'
import NotificationService from '@/v1/modules/notifications/services/notifications-service'
import variables from '@/mixins/variables'

import Button from '@/components/common/Button/Button'
import CancelCampaignUpsertModal from '@/components/campaigns/Modals/CancelCampaignUpsertModal'
import CreativePreviewWrapper from '@/components/creatives/CreativePreview/CreativePreviewWrapper'
import DateTimeInput from '@/components/common/Input/DateTimeInput'
import DestroyCreativeModal from '@/components/campaigns/UpsertForm/DestroyCreativeModal'
import DestroyPixelRuleModal from '@/components/campaigns/UpsertForm/DestroyPixelRuleModal'
import Loader from '@/components/common/Loader/Loader'
import { Money } from 'v-money'
import Multiselect from 'vue-multiselect'
import SelectCreative from '@/components/creatives/SelectCreative'
import SelectCustomTargetingModal from '@/components/custom-targetings/SelectCustomTargetingModal'
import SelectPixelRule from '@/components/pixels/SelectPixelRule'

import { mapState } from 'vuex'
import moment from 'moment'

export default {
  mixins: [variables],
  props: ['baseCampaign'],
  components: {
    Button,
    CancelCampaignUpsertModal,
    CreativePreviewWrapper,
    DateTimeInput,
    DestroyCreativeModal,
    DestroyPixelRuleModal,
    Loader,
    Money,
    Multiselect,
    SelectCreative,
    SelectCustomTargetingModal,
    SelectPixelRule
  },
  data () {
    return {
      autoSaveInterval: null,
      advertisers: [],
      advertisersLoader: false,
      campaign: null,
      categories: [],
      categoriesLoader: false,
      cities: [],
      citiesLoader: false,
      countries: [],
      countriesLoader: false,
      currentCreativeSelection: 'creative',
      form: {
        advertiser_id: '',
        name: '',
        start_date: '',
        end_date: '',
        amount: '',
        cpc_target_amount: 3,
        browser: [],
        creatives: [],
        square_creatives: [],
        type: 'NATIVE_OPEN',
        pixels: [],
        states: [],
        cities: [],
        platform: 'GOOGLE',
        deal_partner: '',
        audiences: [],
        custom_audiences: [],
        allocated: '',
        categories: [],
        blacklist: []
      },
      isSavingDraft: false,
      loader: false,
      minimumDailyBudget: 10,
      money: {
        decimal: ',',
        thousands: '.',
        prefix: 'R$ ',
        precision: 2,
        masked: false
      },
      selectedPixelRule: '',
      selectedCountries: [],
      selectedCreative: '',
      states: [],
      statesLoader: false,
      step: 'init'
    }
  },
  mounted () {
    if (this.isEditPage) {
      this.step = 'campaign'
      this.getCampaign()
    }
    if (this.user) this.getPageContent()
    // this.setAutoSaveInterval()
  },
  beforeDestroy () {
    // this.clearAutoSaveInterval()
  },
  watch: {
    user (newUser) {
      this.getPageContent()
    },
    /**
     * Watch for changes to countries
     */
    countries (newValue, oldValue) {
      // If is edit page, set selected countries
      if (this.isEditPage) this.setSelectedCountries()
    },
    /**
     * Watch for changes to selected countries
     */
    selectedCountries (newValue, oldValue) {
      this.getStates()
      this.getCities()
    }
  },
  computed: {
    ...mapState('users', {
      user: state => state.user
    }),
    /**
     * Get categories options
     */
    categoriesOptions () {
      return this.categories.map(item => {
        return {
          label: `${item.name}`,
          code: item.id
        }
      })
    },
    /**
     *  check if daily budget is bigger than minimum daily budget
     */
    checkIfDailyBudgetIsBiggerThanMinimumDailyBudget () {
      if (this.suggestedEndDate && this.form.amount > 0) {
        const startDate = moment(this.form.start_date)
        const endDate = moment(this.form.end_date)
        const campaignDays = endDate.diff(startDate, 'days') + 1
        const totalBudget = campaignDays * this.minimumDailyBudget

        return this.form.amount >= totalBudget
      }
      return true
    },
    /**
     * Get cities options based on the selected states of the line
     */
    citiesOptions () {
      /* const states = this.form.states.map(state => {
        return state.label
      }) */
      const cities = this.cities.reduce((filtered, item) => {
        /* if (states.includes(item.state_name)) {
          filtered.push({
            label: `${item.name} (${item.state_short_name})`,
            code: item.id
          })
        } */
        filtered.push({
          label: `${item.name} (${item.state_short_name} - ${item.country_short_name})`,
          code: item.id
        })
        return filtered
      }, [])
      return cities
    },
    /**
     * Get countries options
     */
    countryOptions () {
      return this.countries.map(item => {
        return {
          label: `${item.name} (${item.short_name})`,
          code: item.id
        }
      })
    },
    /**
     * Get current creative selection layout given current selected creative modal
     */
    currentCreativeSelectionLayout () {
      switch (this.currentCreativeSelection) {
        case 'creative':
          return '1200x627'
        case 'square_creative':
          return '627x627'
        default:
          return ''
      }
    },
    /**
     * Check if campaign amount has exceeded what advertiser has available
     */
    hasCampaignExceededAvailableAdvertiserAmount () {
      if (!this.selectedAdvertiser) return true
      // If is new campaign, the only thing that matters is the full available amount
      const amount = this.form.amount * 100
      const available = this.selectedAdvertiser.available_amount
      if (!this.campaign) {
        return amount > available
      }

      // If is campaign that already exists, calculate the difference between new amount and old amount and compare that value to available amount
      const difference = amount - this.campaign.amount
      return difference > available
    },
    /**
     * Is campaign end date valid
     */
    isCampaignEndDateValid () {
      if (!this.form.end_date) return false
      const date = moment(this.form.end_date)
      // Check if is update and if value hasnt changed from original
      if (this.campaign && !this.isCampaignDraft) {
        const then = moment(this.campaign.end_date)
        if (date.isSame(then)) return true
      }
      const tomorrow = moment().startOf('day').add(1, 'day')
      return date.isSameOrAfter(tomorrow)
    },
    /**
     * Is campaign status draft
     */
    isCampaignDraft () {
      return this.campaign.status === 'DRAFT'
    },
    /**
     * Checks if campaign end date is before start date
     */
    isCampaignEndDateBeforeStartDate () {
      if (!this.form.end_date || !this.form.start_date) return true
      const startDate = moment(this.form.start_date)
      const endDate = moment(this.form.end_date)

      return endDate.isSameOrBefore(startDate)
    },
    /**
     * Check if campaign period is below recommended
     */
    isCampaignPeriodBelowRecommended () {
      if (!this.form.start_date || !this.form.end_date) return true
      const start = moment(this.form.start_date)
      const end = moment(this.form.end_date)
      return end.diff(start, 'days') < 10
    },
    /**
     * Is campaign start date valid
     */
    isCampaignStartDateValid () {
      if (!this.form.start_date) return false
      const date = moment(this.form.start_date)
      // Check if is update of started campaign and if value hasnt changed from original
      if (this.campaign && !this.isCampaignDraft) {
        const then = moment(this.campaign.start_date)
        if (date.isSame(then)) return true
      }
      const today = moment().endOf('day')
      return date.isAfter(today)
    },
    /**
     * Check if campaign is complete
     */
    isCampaignStepComplete () {
      if (
        !this.form.advertiser_id ||
        !this.form.name ||
        !this.form.start_date ||
        !this.form.end_date ||
        parseFloat(this.form.amount) <= 0 ||
        this.hasCampaignExceededAvailableAdvertiserAmount ||
        parseFloat(this.form.cpc_target_amount) < 3 ||
        !this.isCampaignStartDateValid ||
        !this.isCampaignEndDateValid ||
        this.isCampaignEndDateBeforeStartDate ||
        !this.selectedAdvertiser ||
        this.selectedAdvertiser.status === 'ERROR' ||
        this.form.browser.length === 0
      ) {
        return false
      }

      return true
    },
    /**
     * Check if creative step is complete
     */
    isCreativeStepComplete () {
      return (
        this.form.creatives.length > 0 &&
        this.form.square_creatives.length > 0
      )
    },
    /**
     * Check if is edit page
     */
    isEditPage () {
      return this.$route.name === 'campaigns.edit'
    },
    /**
     * Is form complete
     */
    isFormComplete () {
      return (
        this.isCampaignStepComplete &&
        this.isCreativeStepComplete
      )
    },
    /**
     * Get previous step
     */
    previousStep () {
      switch (this.step) {
        case 'campaign':
          return null
        case 'creative':
          return 'campaign'
        case 'tracking':
          return 'creative'
        default:
          return null
      }
    },
    /**
     * Router name
     */
    routeName () {
      return this.$route.name
    },
    /**
     * Selected advertiser
     */
    selectedAdvertiser () {
      if (!this.form.advertiser_id) return null

      return this.advertisers.find(item => {
        this.money = getMoneyFormat(item.currency)

        return item.id === this.form.advertiser_id
      })
    },
    /**
     * show alert on cities and states selection
     */
    showAlertOnCitiesAndStatesSelection () {
      const selectedStates = this.form.states.map((state) => {
        const stateData = this.states.find((s) => s.id === state.code)
        return stateData?.name || ''
      })

      const selectedCities = this.form.cities.map((city) => {
        const cityData = this.cities.find((c) => c.id === city.code)
        return cityData ? { name: cityData.name, state_name: cityData.state_name } : null
      }).filter(city => city !== null)

      if (selectedStates.length && selectedCities.length) {
        // Check if the cities are within the selected state
        const citiesOutOfState = selectedCities.filter((city) =>
          !selectedStates.includes(city.state_name)
        )

        if (citiesOutOfState.length) {
          return this.$t('region_selection_states_and_cities', {
            states: selectedStates.join(', '),
            cities: [...new Set(citiesOutOfState.map((city) => city.state_name))].join(', ')
          })
        } else {
          return this.$t('region_selection_city_inside_state')
        }
      }

      if (selectedStates.length > 0) {
        return this.$t('region_selection_only_states')
      }

      if (selectedCities.length > 0) {
        return this.$t('region_selection_only_cities')
      }
      return false
    },
    /**
     * State options for select 2 field
     */
    stateOptions () {
      return this.states.map(state => {
        return {
          label: `${state.name} (${state.country_short_name})`,
          code: state.id
        }
      })
    },
    /**
     * Step description
     */
    stepDescription () {
      switch (this.step) {
        case 'campaign':
          return this.$t('create_basic_campaign_settings')
        case 'creative':
          return this.$t('configure_campaign_creatives')
        case 'tracking':
          return this.$t('configure_tracking_pixels')
        default:
          return ''
      }
    },
    /**
     * calculate campaign end date, minimum value 10 reais per day
     */
    suggestedEndDate () {
      if (!this.form.start_date || !this.form.end_date) return null

      const startDate = moment(this.form.start_date)

      const suggestedEndDate = startDate.clone().add(Math.floor(this.form.amount / this.minimumDailyBudget), 'days').subtract(1, 'day')
      return suggestedEndDate.format('YYYY-MM-DDTHH:mm:ss.SSSZ')
    }
  },
  methods: {
    /**
     * Add all selected advertiser available amount to campaign
     */
    addAllAvailableAmountToCampaign () {
      if (!this.selectedAdvertiser) return
      // If is creation, use full amount
      if (!this.isEditPage) {
        this.form.amount = (this.selectedAdvertiser.available_amount / 100)
      } else {
        // If is edit page, add the amount to what is in the form
        this.form.amount = this.form.amount + (this.selectedAdvertiser.available_amount / 100)
      }
    },
    /**
     * Clear form property based on a given parameter
     */
    clearSelection (selection) {
      this.form[selection] = []
    },
    /**
     * Change step of form
     */
    changeStep (step) {
      this.step = step
    },
    /**
     * change accordion item
     */
    changeAccordionItem (item) {
      this.$root.$emit('bv::toggle::collapse', item)
    },
    /**
     * Clear auto save interval
     */
    clearAutoSaveInterval () {
      // this.isAutoSaveEnabled = false
      clearInterval(this.autoSaveInterval)
    },
    /**
     * On click submit button
     */
    clickSubmit () {
      this.submit(false)
    },
    /**
     * Destroy creative
     */
    destroyCreative (creative) {
      if (this.currentCreativeSelection === 'creative') {
        const index = this.form.creatives.findIndex(item => {
          return item.id === creative.id
        })
        if (index >= 0) this.form.creatives.splice(index, 1)
      } else if (this.currentCreativeSelection === 'square_creative') {
        const index = this.form.square_creatives.findIndex(item => {
          return item.id === creative.id
        })
        if (index >= 0) this.form.square_creatives.splice(index, 1)
      }
    },
    /**
     * Destroy custom targetings
     */
    destroyCustomTargetings (index, type) {
      if (type === 'blacklist') {
        this.form.blacklist.splice(index, 1)
      } else if (type === 'custom_audiences') {
        this.form.custom_audiences.splice(index, 1)
      }
    },
    /**
     * Remove pixel rule from rules array
     */
    destroyPixelRule (rule) {
      const index = this.form.pixels.findIndex(item => {
        return item.id === rule.id
      })
      this.form.pixels.splice(index, 1)
    },
    /**
     * Get advertisers
     */
    async getAdvertisers () {
      this.advertisersLoader = true
      try {
        const advertisers = await AdvertisersService.getAdvertisers({
          companyId: this.user.company.id,
          itemsPerPage: 100,
          status: 'ACTIVE',
          viewType: 'listing_for_campaign_creation'
        })
        this.advertisers = advertisers.data
      } catch (error) {
        error.handleGlobally && error.handleGlobally()
      }
      this.advertisersLoader = false
    },
    /**
     * Get advertiser
     */
    async getCampaign () {
      if (!this.baseCampaign) return
      this.campaign = this.baseCampaign
      this.setCampaign(this.baseCampaign)
    },
    /**
     * Get categories
     */
    async getCategories () {
      this.categoriesLoader = true
      try {
        const categories = await CategoriesService.getCategories({
          type: 'CAMPAIGN',
          campaign_type: 'NATIVE'
        })
        this.categories = categories.data
      } catch (error) {
        error.handleGlobally && error.handleGlobally()
      }
      this.categoriesLoader = false
    },
    /**
     * Get cities
     */
    async getCities () {
      if (this.selectedCountries.length === 0) {
        this.cities = []
        return
      }
      this.citiesLoader = true
      try {
        const cities = await HelpersService.getCities({
          countryId: this.selectedCountries.map(item => {
            return item.code
          })
        })
        this.cities = cities
      } catch (error) {
        error.handleGlobally && error.handleGlobally()
      }
      this.citiesLoader = false
    },
    /**
     * Get countries
     */
    async getCountries () {
      this.countriesLoader = true
      try {
        const countries = await HelpersService.getCountries()
        this.countries = countries
      } catch (error) {
        error.handleGlobally && error.handleGlobally()
      }
      this.countriesLoader = false
    },

    /**
     * Get necessary page content
     */
    getPageContent () {
      this.getAdvertisers()
      this.getCountries()
      this.getCategories()
      // this.getStates()
      // this.getCities()
    },
    /**
     * Get states
     */
    async getStates () {
      if (this.selectedCountries.length === 0) {
        this.states = []
        return
      }
      this.statesLoader = true
      try {
        const states = await HelpersService.getStates({
          country_id: this.selectedCountries.map(item => {
            return item.code
          })
        })
        this.states = states
      } catch (error) {
        error.handleGlobally && error.handleGlobally()
      }
      this.statesLoader = false
    },
    /**
     * Handle custom audience selected
     */
    handleCustomAudienceSelected (list) {
      const index = this.form.custom_audiences.findIndex(item => {
        return item.id === list.id
      })
      if (index >= 0) return
      this.form.custom_audiences.push(list)
      this.form.allocated = 50
    },
    /**
     * Handle selected creatives
     */
    handleSelectedCreatives (creatives) {
      creatives.forEach(creative => {
        let targetArray = []
        const layout = creative.layout

        if (layout === '1200x627') {
          targetArray = this.form.creatives
        } else if (layout === '627x627') {
          targetArray = this.form.square_creatives
        } else {
          return // ignora layouts desconhecidos
        }

        const index = targetArray.findIndex(item => item.id === creative.id)
        if (index < 0) {
          targetArray.push(creative)
        }

        // Atualiza o array correto no form
        if (layout === '1200x627') {
          this.form.creatives = targetArray
        } else if (layout === '627x627') {
          this.form.square_creatives = targetArray
        }
      })
      this.currentCreativeSelection = ''
    },
    /**
     * Handle blacklist selected
     */
    handleBlacklistSelected (list) {
      const index = this.form.blacklist.findIndex(item => {
        return item.id === list.id
      })
      if (index >= 0) return
      this.form.blacklist.push(list)
    },
    /**
     * Handle selected pixel rule
     */
    handleSelectedPixelRule (rule) {
      const index = this.form.pixels.findIndex(item => {
        return item.id === rule.id
      })
      if (index >= 0) return
      this.form.pixels.push(rule)
    },
    /**
     * Open destroy creative
     */
    openDestroyCreativeModal (creative, type) {
      this.selectedCreative = creative
      this.currentCreativeSelection = type
      this.$bvModal.show('destroy-creative-modal')
    },
    /**
     * Open modal to destroy pixel rule
     */
    openDestroyPixelRuleModal (rule) {
      this.selectedPixelRule = rule
      this.$bvModal.show('destroy-pixel-rule-modal')
    },
    /**
     * Open select creative modal
     */
    openSelectCreativeModal (type) {
      this.currentCreativeSelection = type
      this.$bvModal.show('select-creative-modal')
    },
    /**
     * Save draft
     */
    saveDraft () {
      if (!this.selectedAdvertiser) return
      if (this.campaign && this.campaign.status !== 'DRAFT') return
      this.submit(true)
    },
    /**
     * Select all states
     */
    selectAllStates () {
      const all = []
      this.stateOptions.forEach(item => {
        all.push({
          label: item.label,
          code: item.code
        })
      })
      this.form.states = all
    },
    /**
     * Create auto save interval
     */
    setAutoSaveInterval () {
      // this.isAutoSaveEnabled = true
      this.autoSaveInterval = setInterval(() => {
        this.saveDraft()
      }, 30000)
    },
    /**
     * Set campaign
     */
    setCampaign (campaign) {
      this.form.advertiser_id = campaign.advertiser.id
      this.form.name = campaign.name
      this.form.start_date = campaign.start_date
      this.form.end_date = campaign.end_date
      this.form.amount = campaign.amount / 100
      this.form.deal_partner = campaign.deal_partner
      this.form.pixels = campaign.pixels.map(item => {
        return {
          id: item.id,
          name: item.name,
          objective: item.objective,
          event_name: item.event_name,
          pixel: item.pixel
        }
      })
      this.form.cpm_target_amount = campaign.cpm_target_amount / 100
      this.form.cpc_target_amount = campaign.cpc_target_amount / 100
      this.form.browser = campaign.browser
      this.form.allocated = campaign.allocated
      this.form.blacklist = campaign.blacklist

      if (this.campaign.is_custom_audiences_enabled) this.form.custom_audiences = campaign.custom_audiences

      const firstPartyAudiences = campaign.audiences.filter(item => item.type === 'FIRST_PARTY')
      this.form.custom_audiences = [...this.form.custom_audiences, ...firstPartyAudiences]

      campaign.creatives.creatives.forEach(creative => {
        this.form.creatives.push(creative)
      })
      campaign.creatives.square_creatives.forEach(creative => {
        this.form.square_creatives.push(creative)
      })
      this.form.categories = campaign.categories.map(s => {
        return {
          label: s.name,
          code: s.id
        }
      })
      this.form.states = campaign.states.map(s => {
        return {
          label: s.name,
          code: s.id
        }
      })
      this.form.cities = campaign.cities.map(c => {
        return {
          label: `${c.name} (${c.state_short_name})`,
          code: c.id
        }
      })
      this.setSelectedCountries()
    },
    /**
     * Set selected countries
     */
    setSelectedCountries () {
      if (!this.campaign) return
      const countries = []
      this.campaign.states.forEach(item => {
        if (!countries.includes(item.country_short_name)) countries.push(item.country_short_name)
      })
      this.campaign.cities.forEach(item => {
        if (!countries.includes(item.country_short_name)) countries.push(item.country_short_name)
      })
      this.selectedCountries = this.countries.filter(item => {
        return countries.includes(item.short_name)
      }).map(item => {
        return {
          label: `${item.name} (${item.short_name})`,
          code: item.id
        }
      })
    },
    /**
     * Submit
     */
    async submit (isDraft = false) {
      // this.clearAutoSaveInterval()
      if (isDraft) this.isSavingDraft = true
      else this.loader = true
      try {
        // Create copy of form that can be changed (object assign wont work for multi-layered objects)
        const form = JSON.parse(JSON.stringify(this.form))
        // Parse amounts
        form.amount = parseInt(form.amount * 100)
        form.cpc_target_amount = parseInt(form.cpc_target_amount * 100)
        form.cities = this.form.cities.map(item => {
          return item.code
        })
        form.states = this.form.states.map(state => {
          return state.code
        })
        form.categories = this.form.categories.map(category => {
          return category.code
        })
        if (form.creatives.length > 0) {
          form.creative_ids = form.creatives.map(item => {
            return item.id
          })
        }
        if (form.square_creatives.length > 0) {
          form.square_creative_ids = form.square_creatives.map(item => {
            return item.id
          })
        }
        form.pixels = this.form.pixels.map(item => {
          return item.id
        })

        form.blacklist = this.form.blacklist.map(item => {
          return item.id
        })

        if (this.selectedAdvertiser.is_custom_audiences_enabled) {
          form.custom_audiences = this.form.custom_audiences.filter(item => item.type === 'EMAIL_AUDIENCE').map(item => {
            return item.id
          })
        }

        const firstPartyAudiences = this.form.custom_audiences.filter(item => item.type === 'FIRST_PARTY').map(item => {
          return item.id
        })
        form.audiences = firstPartyAudiences

        let campaign = null
        form.is_draft = isDraft

        if (!this.campaign) {
          campaign = await CampaignsService.createCampaign(form)
        } else {
          campaign = await CampaignsService.updateCampaign(this.campaign.id, form)
        }
        if (!isDraft) {
          this.$router.push({ name: 'campaigns.show', params: { id: campaign.id } })
          const notification = {
            notification: {
              type: 'success',
              content: this.$t('campaign_in_creation_process')
            }
          }
          NotificationService.createGlobalSuccessNotification(notification)
        } else {
          this.campaign = campaign
        }
      } catch (error) {
        // console.log(error)
        error.handleGlobally && error.handleGlobally()
      }
      if (isDraft) this.isSavingDraft = false
      else this.loader = false
      // Reenable autosave
      // if (isDraft) this.setAutoSaveInterval()
    }
  }
}
</script>

<style lang="css" src="../../../../../node_modules/vue-multiselect/dist/vue-multiselect.min.css"></style>

<style lang="scss" scoped>
  ::v-deep {
    .native-campaign-navigation {
      position: absolute;
      top: 48px;
      left: -100px;
      z-index: 2;
      display: none;

      @include desktop {
        display: block;
      }
    }
  }

  .btn-outline {
    text-transform: none;
  }

  .form-check-custom.form-check-solid .form-check-input {
    border-radius: 50%;
  }

  #budget-allocation::-webkit-slider-thumb {
    background-color: #d8ab44;
  }

  #budget-allocation::-moz-range-thumb {
    background-color: #d8ab44;
  }
</style>
